import {FC, useMemo, useState} from "react";
import {getRelatedTeamIds, useTeamMap} from "@jane/frontend-medewerker/src/components/team-select";
import {usePermissions} from "@jane/frontend-medewerker/src/resources/PermissionsHook";
import {Team} from "../../api";
import {SidebarItem} from "../types/chat-sidebar";
import {useChatContext} from "../hooks/use-chat-context";
import {ChatConversationListItem} from "./ChatConversationListItem";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAdd, faThumbtack, faComments} from "@fortawesome/free-solid-svg-icons";
import {useChatMetadataContext} from "../hooks/use-chat-metadata-context";
import {Select} from "../../components/form/Select";
import {Button} from "../../components/form/Button";
import {TeamSelect} from "./TeamSelect";

interface ChatConversationListProps {
  items: SidebarItem[]
  addChat: () => void

  // Teams filter
  teams: Team[]
  primaryTeam: Team|null
  secondaryTeams: Team[]
}
export const ChatConversationList: FC<ChatConversationListProps> = props => {
  const {canAddChats} = useChatContext()
  const {canAccessEmployees} = usePermissions()
  const {getMetadata} = useChatMetadataContext()
  const [selectedTeams, setSelectedTeams] = useState<Team['id'][]|null>(null)
  const [search, setSearch] = useState('')
  const [paginationLimit, setPaginationLimit] = useState(25)

  const filteredSidebarItems = useMemo(() => canAccessEmployees ? (props.items
    .filter(c => {
      // Dit is de eerste versie van deze fix
      // Uiteindelijk moeten deze chats verwijderd worden. (wanneer niemand ze mist)
      // Zie volgend ticket: #I2504-0126
      return !c.name.startsWith('Fout:');
    })
    .filter(item => {
      // Default, no team is selected
      if (selectedTeams === null) {
        return true
      }
      // Team chat, the conversation's chat-id is selected
      if (item.teamId && selectedTeams.includes(parseInt(item.teamId))) {
        return true
      }
      // Personal chats, at least one explicit member is in a selected team.
      return item.members.filter(m => m.explicit).some((member) => {
        if (member.teamId === null) {
          return false
        }
        return selectedTeams.includes(member.teamId)
      })
    })
  ) : props.items, [canAccessEmployees, props.items, selectedTeams])

  //TODO: Check if c.concern exists in pinnedTeamIds
  const pinnedChatItems = filteredSidebarItems.filter(c => getMetadata(c.conversationId)?.is_favorite)
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => {
      const tA = a.lastMessage?.timestamp.getTime() ?? 0
      const tB = b.lastMessage?.timestamp.getTime() ?? 0
      return tA < tB ? 1 : -1
    })
  const otherChatItems = filteredSidebarItems.filter(c => !getMetadata(c.conversationId)?.is_favorite)
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase())).sort((a,b) => {
      const tA = a.lastMessage?.timestamp.getTime() ?? 0
      const tB = b.lastMessage?.timestamp.getTime() ?? 0
      return tA < tB ? 1 : -1
    })

  // Show all available chats
  return <div className={"w-64 xl:w-96 border-r border-gray-100 overflow-y-scroll"}>
    {props.items.length === 0 ? <div className={"flex flex-col justify-center p-8"}>
      {canAddChats  ? <>
        <span className={"font-medium"}>Je hebt nog geen chats.</span>
        <p className={"text-gray-500 mt-3"}>Start door er een aan te maken. Dit kan voor een team, een specifieke groep, of een-op-een met een collega.</p>
        <Button type={'secondary'} size={'sm'} icon={faAdd} onClick={() => props.addChat()} text={'Maak nieuwe chat'} />
      </> : <>
        <span className={"font-medium"}>U heeft nog geen chats.</span>
        <p className={"text-gray-500 mt-3"}>Om toegang te krijgen tot een chat heeft u een uitnodiging nodig.</p>
      </>}
    </div> : <>
      {/* Add chats */}
      {canAddChats && <div className={"m-2 flex flex-col items-stretch"}><Button type={'secondary'} size={'sm'} icon={faAdd} onClick={() => props.addChat()} text={'Maak nieuwe chat'} /></div>}

      {/* Search chats (coordinators/managers) */}
      {canAccessEmployees && <div className={'px-2 mb-2'}>
        <TeamSelect teams={props.teams} primaryTeam={props.primaryTeam} secondaryTeams={props.secondaryTeams} onTeamsAndSubTeamsChanged={(ids) => setSelectedTeams(ids)} />
      </div>}

      {/* Search chats */}
      <label className={"bg-gray-100 rounded-lg text-gray-800 px-3 py-2 m-2 text-sm flex"}>
        <i className={"bi bi-search"}></i>
        <input type={"search"} value={search} onChange={e => setSearch(e.target.value)} className={"flex-1 outline-hidden bg-transparent ml-2"} placeholder={"Gesprek zoeken..."} />
      </label>
    </>}
    {pinnedChatItems.length > 0 ? <>
      <div className={`flex items-center bg-slate-50 border-t border-slate-200 px-6 h-8 text-sm font-semibold`}>
        <FontAwesomeIcon icon={faThumbtack} className={'mr-2 w-4'} />
        <div className={``}> Favorieten ({pinnedChatItems.length}) </div>
      </div>

      {pinnedChatItems.map((v,k) => {
        return <ChatConversationListItem
          key={k}
          conversationId={v.conversationId}
          name={v.name}
          lastMessage={v.lastMessage}
          unreadCount={v.unread}
          active={v.selected}
          isPurged={v.isPurged}
        />
      })}
    </> : <></>}

    {otherChatItems.length > 0 ? <>
      <div className={`flex items-center bg-slate-50 border-t border-slate-200 px-6 h-8 text-sm font-semibold`}>
        <FontAwesomeIcon icon={faComments} className={'mr-2 w-4'} />
        <div className={``}> Chats ({otherChatItems.length}) </div>
      </div>
      {otherChatItems.slice(0,Math.min(paginationLimit, otherChatItems.length)).map((v,k) => {
        return <ChatConversationListItem
          key={k}
          conversationId={v.conversationId}
          name={v.name}
          lastMessage={v.lastMessage}
          unreadCount={v.unread}
          active={v.selected}
          isPurged={v.isPurged}
        />
      })}
      {paginationLimit < otherChatItems.length ? <>
        <button className={`m-2 rounded-lg px-4 py-3 w-[calc(100%-1rem)] flex items-center justify-center font-medium text-center text-blue-800 hover:text-white hover:bg-blue-500 cursor-pointer`} onClick={() => setPaginationLimit(old => old + 25)}>Meer weergeven ({Math.min(25, otherChatItems.length - paginationLimit)})</button>
      </> : <></>}
    </> : <></>}
  </div>
}
